import queryLoopLoaded from "@plaudit/query-loop.api";

import QueryLoopFormFieldBase from "./QueryLoopFormFieldBase";

export class CheckboxOrRadioQueryLoopFormField extends QueryLoopFormFieldBase {
	private readonly inputs: HTMLInputElement[];

	public constructor(field: HTMLFieldSetElement, type: string) {
		super(field, type);
		const inputs = [];
		for (const input of field.querySelectorAll('.plauditQueryLoopField__input')) {
			if (input instanceof HTMLInputElement) {
				inputs.push(input);
			} else {
				throw new Error("Attempted to attach an invalid field");
			}
		}
		this.inputs = inputs;

		field.addEventListener('click', e => {
			if (e.target instanceof HTMLElement && e.target.classList.contains('plauditQueryLoopField__inputListToggleButton')) {
				const button = e.target;
				const control = button.getAttribute('aria-controls');
				if (!control) {
					return;
				}
				const target = document.getElementById(control);
				if (target) {
					const initialExpanded = button.getAttribute('aria-expanded') === 'true';
					if (initialExpanded) {
						target.setAttribute('aria-hidden', 'true');
						button.setAttribute('aria-expanded', 'false');
					} else {
						target.setAttribute('aria-hidden', 'false');
						button.setAttribute('aria-expanded', 'true');
					}

					button.setAttribute('aria-label',
						button.querySelector<HTMLElement>(`.view${initialExpanded ? 'More' : 'Less'}`)?.innerText || `View ${initialExpanded ? 'More' : 'Less'}`);
				}
				e.preventDefault();
				e.stopPropagation();
			}
		});

		field.addEventListener('change', e => {
			if (e.target instanceof HTMLInputElement) {
				const checked = e.target.checked;
				const duplicateValueInputsSelector = `input[data-value='${e.target.getAttribute("data-value")}']:not(#${CSS.escape(e.target.id)})`;
				for (const input of field.querySelectorAll(duplicateValueInputsSelector)) {
					this.propagateTriStateChange(input, checked, true);
				}
			}
			this.propagateTriStateChange(e.target);
			this.updateTriStateInputs();
		});
		this.updateTriStateInputs();
		this.postConstruct();
	}

	private propagateTriStateChange(target: EventTarget|HTMLInputElement|null, checked?: boolean, orUpdateNormal = false) {
		if (!(target instanceof HTMLInputElement)) {
			return;
		}
		if (checked === undefined) {
			checked = target.checked;
		}
		if (target.hasAttribute('data-tri-state')) {
			const inputs = target.closest('li')?.querySelector('ul')?.querySelectorAll('input');
			if (inputs) {
				for (const input of inputs) {
					this.updateInput(input, checked);
				}
			}
		} else if (orUpdateNormal) {
			this.updateInput(target, checked)
		}
	}

	public getValue(): string|string[] {
		return this.inputs.filter(input => input.checked)
			.map(input => input.getAttribute('data-value') ?? '');
	}
	public setValue(value: string|string[]) {
		if (typeof value === 'string') {
			value = [value];
		}

		let changed = false;
		for (const input of this.getInputsForUpdate()) {
			const checked = value.includes(input.getAttribute('data-value') ?? '');
			if (input.checked !== checked) {
				input.checked = checked;
				changed = true;
				this.propagateTriStateChange(input);
			}
		}
		if (changed) {
			this.updateTriStateInputs();
		}
		return changed;
	}

	public hasVisibleValue(): boolean {
		return this.inputs.some(input => input.checked);
	}

	public loadIntoQuery(params: URLSearchParams) {
		if (this.isSingleValued()) {
			this.replaceMyPotentialPHPArrayParametersWithFirstValue(params);
		}
		const reasonsForVisibleValue = new Set<string>();

		for (const input of this.getInputsForUpdate()) {
			if (input.indeterminate) {
				params.delete(input.name);
			} else {
				const dependencies = input.closest('li')?.querySelectorAll<HTMLInputElement>(':scope > ul > li > span > input');
				if (dependencies) {
					for (const dependency of dependencies) {
						params.delete(dependency.name);
						reasonsForVisibleValue.delete(input.name);
					}
				}

				if (input.checked) {
					if (params.get(input.name) !== input.value) {
						params.set(input.name, input.value);
					}
					reasonsForVisibleValue.add(input.name);
				} else if (params.get(input.name) === input.value) {
					params.delete(input.name);
					reasonsForVisibleValue.delete(input.name);
				}
			}
		}
		return reasonsForVisibleValue.size > 0;
	}
	public loadFromQuery(params: URLSearchParams) {
		let changed = false;
		const expandedParams = new URLSearchParams(params); //TODO: We need to also do this in PHP
		for (const input of this.getInputsForUpdate(-1).filter(input => input.hasAttribute('data-tri-state'))) {
			if (input.checked || expandedParams.get(input.name) === input.value) {
				const dependencies = input.closest('li')?.querySelectorAll<HTMLInputElement>(':scope > ul > li > span > input');
				if (dependencies) {
					for (const dependency of dependencies) {
						expandedParams.set(dependency.name, dependency.value);
					}
				}
			}
		}

		let seenAny = false;
		for (const input of this.inputs) {
			const paramValue = expandedParams.get(input.name);
			if (paramValue === input.value) {
				seenAny = true;
				if (!input.checked) {
					input.checked = true;
					changed = true;
				}
			} else {
				if (paramValue !== null) {
					seenAny = true;
				}
				if (input.checked) {
					input.checked = false;
					changed = true;
				}
			}
		}
		if (!seenAny && this.setValue([''])) {
			changed = true;
		}
		if (changed) {
			this.updateTriStateInputs();
		}
		return changed;
	}

	public getLabeledValues(selectedOnly = false) {
		return (selectedOnly ? this.inputs.filter(input => input.checked) : this.inputs).map(input => {
			const value = input.getAttribute('data-value') ?? '';
			return {field: this.name, value, label: input.getAttribute('data-label') ?? value};
		});
	}

	protected getDefaultValue(): string|string[] {
		return this.inputs.filter(input => input.getAttribute('data-default') === 'true')
			.map(input => input.getAttribute('data-value') ?? '');
	}

	private updateInput(input: HTMLInputElement, checked: boolean|'mixed') {
		if (input.hasAttribute('data-tri-state')) {
			input.ariaChecked = checked.toString();
		} else if (checked === 'mixed') {
			console.error("Attempted to apply a state of 'mixed' to a non-tri-state-input");
		}
		if (checked === 'mixed') {
			input.indeterminate = true;
			input.checked = false;
		} else {
			input.indeterminate = false;
			input.checked = checked;
		}
	}

	private updateTriStateInputs() {
		const updateOrderTriStateInputs = this.getInputsForUpdate()
			.filter(input => input.hasAttribute('data-tri-state'));
		for (const triStateInput of updateOrderTriStateInputs) {
			this.updateInput(triStateInput, this.computeCheckedForTriStateInput(triStateInput));
		}
		this.updateSelectionCounts();
		this.updateValueIsDefaultFlag();
	}
	private computeCheckedForTriStateInput(triStateInput: HTMLInputElement) {
		const inputs = triStateInput.closest('li')?.querySelectorAll<HTMLInputElement>(':scope > ul > li > span > input');
		if (!inputs?.length) {
			return false;
		}
		let childrenChecked: boolean|null = null;
		for (const input of inputs) {
			if (input.ariaChecked === 'mixed') {
				return 'mixed';
			}
			if (childrenChecked === null) {
				childrenChecked = input.checked;
			} else if (childrenChecked !== input.checked) {
				return 'mixed';
			}
		}
		return childrenChecked!; // inputs is never empty, so this will always have a value
	}

	public isSingleValued(): boolean {
		return this.type === 'radio';
	}

	public isInQuery(params = new URLSearchParams(window.location.search)) {
		if (this.type === 'radio') {
			return super.isInQuery(params);
		}
		for (const input of this.inputs) {
			if (params.get(input.name) === input.value) {
				return true;
			}
		}
		return false;
	}

	private getInputsForUpdate(direction: 1|-1 = 1) {
		return this.inputs.sort((a, b) => a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? direction : -direction);
	}
}

queryLoopLoaded.then(() => {
	plaudit.queryLoop.fieldTypes.set('checkbox', field => new CheckboxOrRadioQueryLoopFormField(field, 'checkbox'));
	plaudit.queryLoop.fieldTypes.set('radio', field => new CheckboxOrRadioQueryLoopFormField(field, 'radio'));
});
